import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { Button } from "../../components/ui/Button";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { motion } from "framer-motion";

interface NoReportDialogProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
}

// Move the ModalWrapper component outside to prevent "Component is not a function" error
const ModalWrapper = ({
    onClose,
    title,
}: {
    onClose: () => void;
    title: string;
}) => (
    <div
        className="fixed inset-0 overflow-y-auto overflow-x-hidden flex z-[99999]"
        style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            zIndex: 99999,
            padding: "1rem",
        }}>
        {/* Backdrop */}
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                backdropFilter: "blur(4px)",
            }}
            onClick={onClose}
        />

        {/* Modal Content */}
        <div
            className="bg-white rounded-xl p-5 shadow-2xl max-w-sm w-full relative z-[100000] mx-4 my-auto"
            style={{
                position: "relative",
                zIndex: 100000,
                maxWidth: "24rem",
                width: "calc(100% - 2rem)",
                backgroundColor: "white",
                borderRadius: "0.75rem",
                padding: "1.25rem",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                marginTop: "calc(20vh)",
            }}
            onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}>
                    <h2 className="text-[28px] font-bold text-red-600 mb-5 tracking-tight flex items-center justify-center gap-3">
                        <span
                            className="text-2xl"
                            style={{
                                color: "var(--color-primary)",
                            }}>
                            ❌
                        </span>
                        No Report Available
                    </h2>
                </motion.div>
            </div>

            <div className="flex flex-col items-center space-y-6">
                <motion.div
                    className="w-32 h-32"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}>
                    <DotLottieReact
                        src="https://lottie.host/b53cfe5f-97bb-459d-86b2-f9838448ee80/WtOO1WOLfk.lottie"
                        loop
                        autoplay
                    />
                </motion.div>

                <motion.div
                    className="text-center space-y-4"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}>
                    <p className="text-xl font-semibold text-gray-800">
                        This interview is not completed
                    </p>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-sm text-gray-700">
                            The report for "{title}" will be available once you
                            complete the interview.
                        </p>
                    </div>
                </motion.div>

                <motion.div
                    className="flex gap-3 w-full justify-center pt-4"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}>
                    <Button
                        onClick={onClose}
                        className="min-w-[120px] h-10 text-sm"
                        style={{
                            backgroundColor: "var(--color-primary)",
                            color: "white",
                        }}>
                        Got it
                    </Button>
                </motion.div>
            </div>
        </div>
    </div>
);

export default function NoReportDialog({
    isOpen,
    onClose,
    title,
}: NoReportDialogProps) {
    // Scroll to top when dialog opens
    useEffect(() => {
        if (isOpen) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [isOpen]);

    // If dialog is not open, don't render anything
    if (!isOpen) return null;

    // Create a portal to render the modal at the root level
    return (
        <>
            {typeof document !== "undefined" &&
                ReactDOM.createPortal(
                    <ModalWrapper onClose={onClose} title={title} />,
                    document.body
                )}
        </>
    );
}
